import { yupResolver } from "@hookform/resolvers/yup";
import React, { createRef, forwardRef, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { View, StyleSheet, ScrollView, useWindowDimensions, Dimensions } from "react-native";
import {
  BottomSheet,
  theme,
  Text,
  typography,
  Button,
  Modal as ModalComponent,
  TextInput,
  ITextInput,
  Dropdown,
  Checkbox,
  ErrorMessage,
  ErrorWarningBottomSheet,
} from "ui-components";
import { BankAccountFields } from "../../../enums";
import { translate } from "../../../i18n/translate";
import { addBankAccountSchema } from "../../../screens/new/utils/validation-schemas-payment";
import { useDispatch, useSelector } from "react-redux";
import { setDefaultPaymentSourceUpdated } from "../../../redux/actions/actionCreators";
import { RootState } from "../../../redux/reducers";
import { PaymentSourceService } from "../../../api";
import { DropDownChoice } from "../../../../types/components/dropdown";
import automationTestingLocators from "../../../screens/new/utils/automation-testing-locators.json";
import Auryc from "rn-auryc-sdk";

// Labels used to show to users and values is used to be send as an API parameter.
const accountTypeDropdownChoices: DropDownChoice[] = [
  { label: "Select Account Type", value: "selectAccountType", dbValue: "" },
  { label: "Checking", value: "Checking", dbValue: "CHECKING" },
  { label: "Savings", value: "Savings", dbValue: "SAVINGS" },
  { label: "Corporate Checking", value: "Corporate", dbValue: "CORPORATE" },
  { label: "Corporate Savings", value: "Corporate Savings", dbValue: "CORP_SAVINGS" },
];

const webMaxWidth = 1200;
const tabMaxWidth = 992;
const mobileWebMaxWidth = 768;
const mobileMaxWidth = 576;

const {
  ACCOUNT_HOLDER,
  ACCOUNT_TYPE,
  ROUTING_NUMBER,
  ACCOUNT_NUMBER,
  CONFIRM_ACCOUNT_NUMBER,
  NICKNAME,
} = BankAccountFields;

interface AddPaymentBAProps {
  height?: number | string;
  children?: any;
  screenWidth?: number;
  reservationId?: any;
  isDomusoCash?: any;
  fromSelectPayment?: any;
  updated?: any;
  toggleForBankBottomSheet: Boolean;
  showStoreBankCheckbox?: boolean;
}

export const AddPaymentBA = forwardRef(
  (
    {
      children,
      height,
      reservationId,
      isDomusoCash,
      updated,
      toggleForBankBottomSheet,
      showStoreBankCheckbox = true,
    }: AddPaymentBAProps,
    ref: any
  ) => {
    const { width: screenWidth } = useWindowDimensions();
    const { height: screenHeight } = Dimensions.get("window");
    const dispatch = useDispatch();
    const styles = StyleSheet.create({
      checkboxStyle: {
        width: 20,
        height: 20,
      },
      checkboxTextStyle: {
        ...typography.title2,
        color: theme.colors.text3,
        marginLeft: 10,
        flex: 1,
      },
    });
    const [loading, setLoading] = useState(false);
    const [selectedAccountTypeValue, setSelectedAccountTypeValue] = useState();
    const [shouldStoreBankAccount, setShouldStoreBankAccount] = useState(true);
    const [accountHolderState, setAccountHolderState] = useState("");
    const [routingNumberState, setRoutingNumberState] = useState("");
    const [accountNumberState, setAccountNumberState] = useState("");
    const [confirmAccountNumberState, setConfirmAccountNumberState] = useState("");
    const [nicknameState, setNicknameState] = useState("");

    let CustomTag = screenWidth <= mobileMaxWidth ? BottomSheet : ModalComponent;
    const {
      register,
      handleSubmit,
      setValue: originalSetValue,
      reset,
      getValues,
      clearErrors,
      formState: { errors },
    } = useForm({
      resolver: yupResolver(addBankAccountSchema),
    });
    const setValue = (formName: string, val: any) => {
      clearErrors(formName);
      originalSetValue(formName, val);
      if (formName == ACCOUNT_HOLDER) {
        setAccountHolderState(val);
      } else if (formName == ROUTING_NUMBER) {
        setRoutingNumberState(val);
      } else if (formName == ACCOUNT_NUMBER) {
        setAccountNumberState(val);
      } else if (formName == CONFIRM_ACCOUNT_NUMBER) {
        setConfirmAccountNumberState(val);
      } else if (formName == NICKNAME) {
        setNicknameState(val);
      }
    };
    const cognitoUser = useSelector((state) => state.auth.cognitoUser);
    const [apiError, setApiError] = React.useState<any>(false);
    const service = new PaymentSourceService(cognitoUser, dispatch);
    const inputAccessoryViewID = "inputAccessoryViewID2";
    const textInputRef = [
      createRef(),
      createRef(),
      createRef(),
      createRef(),
      createRef(),
      createRef(),
    ];
    const errorPopup = useRef();
    const abortController = useRef<AbortController>(null);

    const [currentInputIndex, setCurrentInputIndex] = useState(0);
    const logger = useSelector((state: RootState) => state.logger);
    const accountTypeToDBFieldValueMapping = (accountTypeValue: string) => {
      let dbValue = null;
      for (let accountType of accountTypeDropdownChoices) {
        if (accountType.value === accountTypeValue) {
          dbValue = accountType.dbValue;
          break;
        }
      }
      return dbValue;
    };

    const resetFields = () => {
      setLoading(false);
      reset();
      setAccountHolderState("");
      setRoutingNumberState("");
      setAccountNumberState("");
      setConfirmAccountNumberState("");
      setNicknameState("");
      setSelectedAccountTypeValue(undefined);
    };

    useEffect(() => {
      /*
        reset form values every time bottom-sheet opens 
      */
      resetFields();
    }, [toggleForBankBottomSheet]);

    useEffect(() => {
      register(ACCOUNT_HOLDER);
      register(ACCOUNT_TYPE);
      register(ROUTING_NUMBER);
      register(ACCOUNT_NUMBER);
      register(CONFIRM_ACCOUNT_NUMBER);
      register(NICKNAME);
    }, [register]);

    const onSubmit = async (data: any) => {
      setLoading(true);
      setApiError(null);
      const {
        routingNumber: paymentRoutingNumber,
        accountNumber: bankAccountNumber,
        accountType: bankAccountType,
        accountHolder,
      } = data;
      abortController.current = new AbortController();
      service
        .addPaymentMethodBA(
          {
            ...data,
            bankAccountType: accountTypeToDBFieldValueMapping(bankAccountType),
            cardholder: accountHolder,
            allowCof: !!shouldStoreBankAccount,
            reservationId,
            paymentSourceType: "BANK_ACCOUNT",
            paymentRoutingNumber,
            bankAccountNumber,
          },
          abortController?.current?.signal
        )
        .then((resp) => {
          updated();
          const { payment_source_id: paymentSourceId } = resp;
          ref?.current?.close();
          dispatch(setDefaultPaymentSourceUpdated(paymentSourceId));
          resetFields();
          setSelectedAccountTypeValue(undefined);
          return;
        })
        .catch((e) => {
          logger.error(e);
          const { message } = e;
          if (message !== "Aborted") {
            if (e?.message == "Failed to fetch" || e?.message == "Network request failed") {
              setApiError(translate("addPaymentBa.connectionErrorMessage") as string);
            } else {
              setApiError(message);
            }
            errorPopup?.current?.open();
          }
          setLoading(false);
          resetFields();
          setSelectedAccountTypeValue(undefined);
        });
      return;
    };
    const cancelRequest = () => abortController.current && abortController.current.abort();

    return (
      <CustomTag
        ref={ref}
        height={height || 550}
        showCloseIcon={true}
        containerStyle={{
          width:
            screenWidth <= mobileMaxWidth
              ? "100%"
              : screenWidth >= mobileMaxWidth && screenWidth <= webMaxWidth
              ? 400
              : 500,
        }}
        onDismiss={() => {
          cancelRequest();
        }}
        {...automationTestingLocators.addPaymentBa.popup}>
        <View
          style={screenWidth <= mobileMaxWidth ? { flex: 1 } : { maxHeight: screenHeight - 100 }}>
          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1 }}>
            <Text
              style={[
                typography.title1,
                { fontFamily: "Lato_900Black", color: theme.colors.secondary },
              ]}
              {...automationTestingLocators.addPaymentBa.heading}>
              {translate("addPaymentBa.title")}
            </Text>

            <>
              <View style={{ margin: 8 }} />
              <LongRow
                value={accountHolderState}
                styles={styles}
                setValue={setValue}
                errors={errors}
                formName={ACCOUNT_HOLDER}
                inputAccessoryViewID={inputAccessoryViewID}
                ref={textInputRef[0] as any}
                onFocus={() => setCurrentInputIndex(0)}
                placeholder="e.g: Alex Perry"
                isSensitiveInformation={false}
                {...automationTestingLocators.addPaymentBa.accountHolder}
              />
              <View style={{ margin: 6 }} />

              <View style={{ flexDirection: "row" }}>
                <View style={{ flex: 1 }}>
                  <Dropdown
                    textInputProps={{
                      placeholder: translate("addPaymentBa.accountTypePlaceholder") as string,
                    }}
                    label={translate(`addPaymentBa.accountType`) as string}
                    pickerItem={accountTypeDropdownChoices}
                    onValueChange={(value, index) => {
                      if (value != "selectAccountType") {
                        setSelectedAccountTypeValue(value);
                        setValue(ACCOUNT_TYPE, value);
                      }
                    }}
                    selectedValue={selectedAccountTypeValue}
                    labelStyle={styles.label}
                    {...automationTestingLocators.addPaymentBa.accountTypeDropdown}
                  />
                  <View style={{ marginLeft: -8 }}>
                    <ErrorMessage
                      errors={errors}
                      style={{ fontFamily: "Lato_700Bold" }}
                      formName={ACCOUNT_TYPE}
                    />
                  </View>
                </View>
              </View>
              <View style={{ margin: 6 }} />
              <View>
                <LongRow
                  value={routingNumberState}
                  styles={styles}
                  setValue={setValue}
                  errors={errors}
                  keyboardType="number-pad"
                  maxLength={9}
                  formName={ROUTING_NUMBER}
                  inputAccessoryViewID={inputAccessoryViewID}
                  ref={textInputRef[2] as any}
                  onFocus={() => setCurrentInputIndex(2)}
                  onSubmitEditing={() => (textInputRef[3].current as any).focus()}
                  returnKeyType="next"
                  placeholder="xxxxxxxxxx"
                  showInfoIcon={true}
                  tooltipTitle={translate("addPaymentBa.routingNumber") as string}
                  tooltipBody={translate("addPaymentBa.routingNumberInfo") as string}
                  tooltipWebWidth={325}
                  isSensitiveInformation={false}
                  {...automationTestingLocators.addPaymentBa.routingNumber}
                  dataSet={{ heapRedactText: "" }}
                />
              </View>
              <View style={{ margin: 6 }} />
              <View>
                <LongRow
                  value={accountNumberState}
                  styles={styles}
                  setValue={setValue}
                  keyboardType="number-pad"
                  errors={errors}
                  maxLength={20}
                  formName={ACCOUNT_NUMBER}
                  inputAccessoryViewID={inputAccessoryViewID}
                  ref={textInputRef[3] as any}
                  onFocus={() => setCurrentInputIndex(3)}
                  onSubmitEditing={() => (textInputRef[4].current as any).focus()}
                  returnKeyType="next"
                  placeholder="xxxxxxxxxxxxxx"
                  showInfoIcon={true}
                  tooltipTitle={translate("addPaymentBa.accountNumber") as string}
                  tooltipBody={translate("addPaymentBa.accountNumberInfo") as string}
                  tooltipWebWidth={325}
                  isSensitiveInformation={true}
                  {...automationTestingLocators.addPaymentBa.accountNumber}
                  dataSet={{ heapRedactText: "" }}
                />
              </View>
            </>
            <>
              <View style={{ margin: 6 }} />
              <View>
                <LongRow
                  value={confirmAccountNumberState}
                  styles={styles}
                  setValue={setValue}
                  keyboardType="number-pad"
                  errors={errors}
                  maxLength={20}
                  formName={CONFIRM_ACCOUNT_NUMBER}
                  inputAccessoryViewID={inputAccessoryViewID}
                  ref={textInputRef[4] as any}
                  onFocus={() => setCurrentInputIndex(4)}
                  onSubmitEditing={() =>
                    shouldStoreBankAccount ? (textInputRef[5].current as any).focus() : {}
                  }
                  returnKeyType="next"
                  placeholder="xxxxxxxxxxxxxx"
                  isSensitiveInformation={true}
                  {...automationTestingLocators.addPaymentBa.confirmAccountNumber}
                  dataSet={{ heapRedactText: "" }}
                />
              </View>
              {showStoreBankCheckbox && (
                <View style={{ margin: shouldStoreBankAccount ? 8 : 12 }} />
              )}
              {showStoreBankCheckbox && (
                <View style={{ flexDirection: "row" }}>
                  <View style={{ paddingTop: 3 }}>
                    <Checkbox
                      style={styles.checkboxStyle}
                      onValueChange={(newValue: boolean) => {
                        setShouldStoreBankAccount(newValue);
                      }}
                      value={shouldStoreBankAccount}
                      {...automationTestingLocators.addPaymentBa.storeBankAccountCheckbox}
                    />
                  </View>
                  <Text
                    style={styles.checkboxTextStyle}
                    {...automationTestingLocators.addPaymentBa.storeBankAccountText}>
                    {`${translate("addPaymentBa.storeBankAccountText")}`}
                  </Text>
                </View>
              )}
              {shouldStoreBankAccount && (
                <>
                  <View style={{ margin: 8 }} />

                  <LongRow
                    value={nicknameState}
                    styles={styles}
                    errors={errors}
                    setValue={setValue}
                    formName={NICKNAME}
                    inputAccessoryViewID={inputAccessoryViewID}
                    ref={textInputRef[5] as any}
                    onFocus={() => setCurrentInputIndex(5)}
                    onSubmitEditing={() => {
                      handleSubmit(onSubmit)();
                    }}
                    returnKeyType="go"
                    placeholder="e.g: Savings account"
                    maxLength={27}
                    isSensitiveInformation={false}
                    {...automationTestingLocators.addPaymentBa.nickname}
                  />
                </>
              )}
            </>

            <View style={{ margin: shouldStoreBankAccount ? 15 : 20 }} />
            <Button
              text={translate("addPaymentBa.saveAndContinue") as string}
              mode="contained"
              onPress={() => {
                handleSubmit(onSubmit)();
              }}
              textStyle={{ fontFamily: "Lato_700Bold" }}
              loading={loading}
              disabled={loading}
              {...automationTestingLocators.addPaymentBa.continueButton}
            />
            <View style={{ marginBottom: 10 }} />
          </ScrollView>
        </View>

        <ErrorWarningBottomSheet
          ref={errorPopup}
          title={translate("addPaymentBa.error") as string}
          body={apiError && apiError}
          height={170}
          alertType={"error"}
          showButtons={false}
          {...automationTestingLocators.addPaymentBa.errorWarningAlert}
        />
      </CustomTag>
    );
  }
);

interface LongRowProps extends ITextInput {
  styles: any;
  setValue: any;
  formName: string;
  errors?: Record<string, any>;
}
const LongRow = forwardRef(
  (
    {
      styles,
      setValue,
      formName,
      placeholder,
      errors,
      render,
      maxLength,
      keyboardType = "default",
      returnKeyType,
      inputAccessoryViewID,
      onFocus,
      onSubmitEditing,
      value,
      showInfoIcon,
      tooltipTitle,
      tooltipBody,
      isSensitiveInformation,
      ...props
    }: LongRowProps,
    ref: any
  ) => {
    return (
      <View
        accessible={true}
        ref={(x) =>
          isSensitiveInformation
            ? Auryc.markViewAsSensitiveInformation(x)
            : Auryc.unMarkViewAsSensitiveInformation(x)
        }>
        <TextInput
          value={value}
          labelStyle={{ fontFamily: "Lato_700Bold" }}
          label={translate(`addPaymentBa.${formName}`)}
          error={errors?.[formName]}
          mode="outlined"
          render={render}
          keyboardType={keyboardType}
          maxLength={maxLength}
          placeholder={placeholder}
          onChangeText={(text) => setValue(formName, text)}
          ref={ref}
          returnKeyType={returnKeyType}
          inputAccessoryViewID={inputAccessoryViewID}
          onFocus={onFocus}
          onSubmitEditing={onSubmitEditing}
          showInfoIcon={showInfoIcon}
          tooltipTitle={tooltipTitle}
          tooltipBody={tooltipBody}
          tooltipWebWidth={325}
          {...props}
        />
      </View>
    );
  }
);
